import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const WRITE_PERMS = {
  orders: ["admin", "production_manager"],
  production: ["admin", "production_manager", "supervisor", "operator"],
  samples: ["admin", "production_manager", "supervisor"],
  qc: ["admin", "production_manager", "qc"],
  machines: ["admin", "production_manager", "supervisor"],
  packing: ["admin", "production_manager", "packing_staff"],
  dispatch: ["admin", "production_manager", "packing_staff"],
  employees: ["admin", "production_manager"],
  customers: ["admin", "production_manager"],
  products: ["admin", "production_manager"],
  planning: ["admin", "production_manager"],
  rnd: ["admin", "production_manager"],
  restock: ["admin", "production_manager"],
  announce: ["admin", "production_manager"],
  masters: ["admin"],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (!err?.response || err.response.status !== 401) {
        console.error("Auth check failed:", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("Logout request failed:", err); }
    localStorage.removeItem("session_token");
    setUser(null);
    window.location.href = "/login";
  };

  const canWrite = (resource) => {
    if (!user) return false;
    return (WRITE_PERMS[resource] || ["admin"]).includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, canWrite }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
